<template>
  <div class="filters-bar" style="margin: 0px 20px;">
    <!-- Filter dropdowns -->
    <div class="d-flex flex-wrap align-items-center">
      <div
        v-for="field in filtersAvailable"
        :key="field.key"
        class="filter-dropdown position-relative me-2 mb-2"
      >
        <button
          type="button"
          class="btn btn-sm mb-0 d-flex align-items-center"
          :class="selectedValues(field.key).length ? 'bg-gradient-info text-white' : 'btn-outline-secondary'"
          @click.stop="toggle(field.key)"
        >
          {{ field.label }}
          <span
            v-if="selectedValues(field.key).length"
            class="badge bg-white text-info ms-2"
          >
            {{ selectedValues(field.key).length }}
          </span>
          <i class="material-icons-round notranslate ms-1" style="font-size: 18px;">
            {{ openKey === field.key ? "expand_less" : "expand_more" }}
          </i>
        </button>

        <!-- Options panel -->
        <div
          v-if="openKey === field.key"
          class="filter-panel card shadow border"
          @click.stop
        >
          <div class="card-body p-2">
            <div class="filter-options">
              <label
                v-for="opt in field.options"
                :key="opt.value"
                class="d-flex align-items-center py-1 px-2 mb-0 filter-option"
              >
                <input
                  type="checkbox"
                  class="form-check-input mt-0 me-2"
                  :checked="isChecked(field.key, opt.value)"
                  @change="toggleValue(field.key, opt.value)"
                />
                <span class="text-sm text-dark">{{ opt.label }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="hasAnyActive"
        type="button"
        class="btn btn-sm btn-link text-danger mb-2 ms-1"
        @click="clearAll"
      >
        Limpar tudo
      </button>
    </div>

    <!-- Active filter chips -->
    <div v-if="hasAnyActive" class="d-flex flex-wrap mt-1 mb-2">
      <span
        v-for="chip in activeChips"
        :key="`${chip.fieldKey}-${chip.value}`"
        class="badge bg-gradient-info d-flex align-items-center me-2 mb-2"
      >
        {{ chip.fieldLabel }}: {{ chip.optionLabel }}
        <i
          class="material-icons-round notranslate ms-1"
          style="font-size: 14px; cursor: pointer;"
          @click="removeChip(chip.fieldKey, chip.value)"
        >
          close
        </i>
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "ParticipantsContentTreeFilters",

  props: {
    filtersAvailable: {
      type: Array,
      required: true,
    },
  },

  emits: ["apply"],

  data() {
    return {
      // { "<field_id>": [value, ...] }
      selected: {},
      openKey: null,
      // Snapshot of the last emitted filters, to detect real changes on close.
      lastApplied: "{}",
    };
  },

  computed: {
    hasAnyActive() {
      return Object.values(this.selected).some((values) => values.length > 0);
    },

    activeChips() {
      const chips = [];
      this.filtersAvailable.forEach((field) => {
        const values = this.selectedValues(field.key);
        values.forEach((value) => {
          const option = field.options.find((opt) => opt.value === value);
          chips.push({
            fieldKey: field.key,
            fieldLabel: field.label,
            value,
            optionLabel: option ? option.label : value,
          });
        });
      });
      return chips;
    },
  },

  mounted() {
    // Clicking outside any dropdown closes it (and applies if changed).
    document.addEventListener("click", this.closeCurrent);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.closeCurrent);
  },

  methods: {
    selectedValues(fieldKey) {
      return this.selected[String(fieldKey)] || [];
    },

    isChecked(fieldKey, value) {
      return this.selectedValues(fieldKey).includes(value);
    },

    toggle(fieldKey) {
      if (this.openKey === fieldKey) {
        this.closeCurrent();
      } else {
        // Switching from another open dropdown also applies its pending change.
        this.applyIfChanged();
        this.openKey = fieldKey;
      }
    },

    closeCurrent() {
      if (this.openKey === null) return;
      this.openKey = null;
      this.applyIfChanged();
    },

    toggleValue(fieldKey, value) {
      const key = String(fieldKey);
      const current = this.selected[key] ? [...this.selected[key]] : [];
      const index = current.indexOf(value);
      if (index === -1) {
        current.push(value);
      } else {
        current.splice(index, 1);
      }
      // Reassign to keep reactivity clean and drop empty fields.
      const next = { ...this.selected };
      if (current.length) {
        next[key] = current;
      } else {
        delete next[key];
      }
      this.selected = next;
    },

    removeChip(fieldKey, value) {
      this.toggleValue(fieldKey, value);
      // Chip removal is a direct action — apply immediately.
      this.applyIfChanged();
    },

    clearAll() {
      this.selected = {};
      this.openKey = null;
      this.applyIfChanged();
    },

    // Build the request filters object: omit fields with no selection.
    buildFilters() {
      const filters = {};
      Object.keys(this.selected).forEach((key) => {
        if (this.selected[key] && this.selected[key].length) {
          filters[key] = this.selected[key];
        }
      });
      return filters;
    },

    applyIfChanged() {
      const filters = this.buildFilters();
      const serialized = JSON.stringify(filters);
      if (serialized === this.lastApplied) return;
      this.lastApplied = serialized;
      this.$emit("apply", filters);
    },
  },
};
</script>

<style scoped>
.filter-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 280px;
  overflow-y: auto;
}

.filter-option {
  cursor: pointer;
  border-radius: 0.375rem;
}

.filter-option:hover {
  background-color: #f0f8ff;
}
</style>
